import { Check, X, Award } from 'lucide-react';

interface Props {
  score: {
    total: number;
    correct: number;
    percentage: number;
  };
  onRetry?: () => void;
  onClose: () => void;
}

export default function QuizResults({ score, onRetry, onClose }: Props) {
  const rating = score.percentage >= 80 ? 'excellent' : score.percentage >= 60 ? 'good' : score.percentage >= 40 ? 'fair' : 'needs_improvement';

  const ratingConfig = {
    excellent: { label: 'Excellent!', color: '#00A86B', bg: 'rgba(0, 168, 107, 0.15)' },
    good: { label: 'Good Job!', color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.15)' },
    fair: { label: 'Fair', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.15)' },
    needs_improvement: { label: 'Needs Improvement', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)' },
  };

  const config = ratingConfig[rating];

  return (
    <div className="quiz-results">
      <div className="results-compact" style={{ background: config.bg, borderColor: config.color }}>
        <div className="results-score-ring">
          <svg viewBox="0 0 100 100" width="80" height="80">
            <circle cx="50" cy="50" r="42" fill="none" stroke="var(--border, #4A4A4A)" strokeWidth="6" />
            <circle
              cx="50" cy="50" r="42"
              fill="none"
              stroke={config.color}
              strokeWidth="6"
              strokeLinecap="round"
              strokeDasharray={`${(score.percentage / 100) * 264} 264`}
              transform="rotate(-90 50 50)"
            />
            <text x="50" y="46" textAnchor="middle" fill="var(--text-primary, #fff)" fontSize="22" fontWeight="700">
              {Math.round(score.percentage)}%
            </text>
            <text x="50" y="62" textAnchor="middle" fill="var(--text-secondary, #ccc)" fontSize="10">
              Score
            </text>
          </svg>
        </div>

        <div className="results-info">
          <div className="results-rating">
            <Award size={20} style={{ color: config.color }} />
            <h3 style={{ color: config.color }}>{config.label}</h3>
          </div>

          <div className="results-breakdown">
            <div className="breakdown-item correct">
              <Check size={14} />
              <span>{score.correct} correct</span>
            </div>
            <div className="breakdown-item incorrect">
              <X size={14} />
              <span>{score.total - score.correct} incorrect</span>
            </div>
            <span className="breakdown-total">{score.total} total</span>
          </div>

          <div className="results-actions">
            {onRetry && (
              <button className="retry-btn" onClick={onRetry}>Retry Quiz</button>
            )}
            <button className="close-btn" onClick={onClose}>Review Answers</button>
          </div>
        </div>
      </div>

      <style>{`
        .quiz-results {
          padding: 0 0 8px 0;
        }
        .results-compact {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 16px 20px;
          border-radius: 12px;
          border: 1px solid;
        }
        .results-score-ring {
          flex-shrink: 0;
        }
        .results-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .results-rating {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .results-rating h3 {
          margin: 0;
          font-size: 18px;
        }
        .results-breakdown {
          display: flex;
          align-items: center;
          gap: 14px;
        }
        .breakdown-item {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 13px;
          color: var(--text-secondary);
        }
        .breakdown-item.correct svg { color: #00A86B; }
        .breakdown-item.incorrect svg { color: #EF4444; }
        .breakdown-total {
          font-size: 12px;
          color: var(--text-muted);
        }
        .results-actions {
          display: flex;
          gap: 10px;
          margin-top: 4px;
        }
        .retry-btn {
          background: transparent;
          border: 2px solid var(--primary, #FF5A36);
          color: var(--primary, #FF5A36);
          padding: 8px 18px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .retry-btn:hover {
          background: rgba(255, 90, 54, 0.1);
        }
        .close-btn {
          background: var(--primary, #FF5A36);
          color: white;
          border: none;
          padding: 8px 18px;
          border-radius: 8px;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        .close-btn:hover {
          opacity: 0.9;
        }
      `}</style>
    </div>
  );
}
